














































































































































import {
  computed,
  defineComponent,
  ref,
  ssrRef,
  useContext,
  useFetch,
  useRouter,
  watch,
} from "@nuxtjs/composition-api";
import { CacheTagPrefix, useCache } from "@vue-storefront/cache";
import { decodeUid } from "~/helpers";
import { usePageStore } from "~/stores/page";
import { useTraverseCategory } from "~/modules/catalog/category/helpers/useTraverseCategory";
import { useCategoryPageStore } from "~/modules/catalog/category/stores/categoryPage";
import { useUiHelpers, useSearchProducts } from "~/composables";
import type { ProductInterface } from "~/modules/GraphQL/types";
import type { SortingModel } from "~/modules/catalog/category/composables/useFacet/sortingOptions";
import type { RemovableFilterInterface } from "~/modules/catalog/category/components/filters/useFilters";
import ProductLoadItem from "~/components/SkeletonLoader/ProductLoadItem.vue";
import CategoryNavbar from "~/modules/catalog/category/components/navbar/CategoryNavbarNew.vue";
import CategoryNavbarMobile from "~/modules/catalog/category/components/navbar/CategoryNavbarMobile.vue";
import FilterNavBar from "~/modules/catalog/category/components/filters/FilterNavBar/FilterNavBar.vue";
import { useClevertapStore } from "~/stores/clevertap";
import { debounce } from "~/helpers/debounce";
import { useCategoryStore } from "~/modules/catalog/category/stores/category";
import { findCategoryByUid } from "~/modules/catalog/category/helpers";

export default defineComponent({
  name: "CanifaS",
  components: {
    ProductLoadItem,
    CategoryNavbar,
    CategoryNavbarMobile,
    FilterNavBar,
    CategoryEmptyResults: () =>
      import("~/modules/catalog/category/components/CategoryEmptyResults.vue"),
    CategoryFilters: () =>
      import(
        "~/modules/catalog/category/components/filters/CategoryFiltersNew.vue"
      ),
    CategoryProductGrid: () =>
      import(
        "~/modules/catalog/category/components/views/CategoryProductGrid.vue"
      ),
    CategoryFiltersMobile: () =>
      import(
        "~/modules/catalog/category/components/filters/CategoryFiltersMobile.vue"
      ),
  },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const router = useRouter();
    const { routeData } = usePageStore();
    const { fetchProducts, loading: loadingProduct } = useSearchProducts();
    const { addTags } = useCache();
    const uiHelpers = useUiHelpers();
    const clevertapStore = useClevertapStore();
    const categoryStore = useCategoryStore();
    const categoryTree = computed(() => categoryStore.categories);

    const { activeCategoryById } = useTraverseCategory();
    const activeCategoryByUid = computed(() => {
      return activeCategoryById(decodeUid(routeData?.uid));
    });
    const categoryUrlPath = computed(() => {
      return activeCategoryByUid.value?.url_path || "";
    });
    const categoryPageStore = useCategoryPageStore();

    const leftMenu = computed(() => {
      return props.item.category_url || [];
    });

    const activeIndexes = ref<number[]>([]);

    const toggleActive = (index) => {
      const i = activeIndexes.value?.indexOf(index);
      if (i > -1) {
        activeIndexes.value?.splice(i, 1);
      } else {
        activeIndexes.value?.push(index);
      }
    };

    const categoryUid = routeData.uid as string;
    const categoryId = decodeUid(categoryUid);
    const categoryFiltersRef = ref(null);
    const categoryIdKey = computed(() => String(categoryUid || "9999"));
    const currentPage = computed(
      () => categoryPageStore.getCategoryPage(categoryIdKey.value) || 1
    );
    const loading = ref(true);
    const products = ssrRef<ProductInterface[]>([]);
    const sortBy = ref<SortingModel>({ selected: "", options: [] });
    const totalItems = ref(0);
    const productContainerElement = ref<HTMLElement | null>(null);
    const isOpenFilterMobile = ref(false);

    const { fetch } = useFetch(async () => {
      if (!activeCategoryByUid.value) return;

      loading.value = true;

      const params = {
        ids: [String(categoryId)],
        pageSize: 24 * currentPage.value,
        filters: getParamFilter(),
      };
      const { data, total } = await fetchProducts(params);
      totalItems.value = total || 0;
      products.value = data || [];

      loading.value = false;

      const tags = [{ prefix: CacheTagPrefix.View, value: routeData.uid }];
      const productTags = products.value?.map((product) => ({
        prefix: CacheTagPrefix.Product,
        value: product?.uid,
      }));

      addTags([...tags, ...productTags]);
    });

    const getParamFilter = () => {
      let routerQuery;
      if (process?.client) {
        routerQuery = router?.resolve(
          (window?.location?.pathname + window?.location?.search)?.slice(1)
        )?.route?.query;
      } else {
        routerQuery = router?.currentRoute?.query;
      }
      let paramFilter: any = {};

      if (routerQuery) {
        if (routerQuery.size) {
          paramFilter.sizeIds = Array.isArray(routerQuery.size)
            ? routerQuery.size
            : [routerQuery.size];
        }

        if (routerQuery.price) {
          paramFilter.priceRanger = Array.isArray(routerQuery.price)
            ? routerQuery.price
            : routerQuery.price.split("_");
        }

        if (routerQuery.mastercolor) {
          paramFilter.colorIds = Array.isArray(routerQuery.mastercolor)
            ? routerQuery.mastercolor
            : [routerQuery.mastercolor];
        }

        if (routerQuery.sort && routerQuery.sort == "created_at_DESC") {
          paramFilter.order = "desc";
          paramFilter.orderby = "created_at";
        }

        if (routerQuery.sort && routerQuery.sort == "price_ASC") {
          paramFilter.order = "asc";
          paramFilter.orderby = "final_price";
        }

        if (routerQuery.sort && routerQuery.sort == "price_DESC") {
          paramFilter.order = "desc";
          paramFilter.orderby = "final_price";
        }

        return paramFilter;
      }
    };

    const isLoadingMore = ref(false);
    const loadMoreProducts = async () => {
      isLoadingMore.value = true;
      const nextPage = currentPage.value + 1;
      categoryPageStore.setCategoryPage(categoryIdKey.value, nextPage);

      const params = {
        ids: [String(categoryId)],
        pageSize: 24,
        currentPage: nextPage,
        filters: getParamFilter(),
      };
      const { data } = await fetchProducts(params);

      const newProducts = data ?? [];
      products.value = [...products.value, ...newProducts];
      isLoadingMore.value = false;
    };

    const onReloadProducts = () => {
      categoryPageStore.resetCategoryPage(categoryIdKey.value);
      goToPage(0);
      scrollToTop();
    };

    const goToPage = (page: number) => {
      uiHelpers.changePage(page, false);

      fetch();
    };

    const scrollToTop = () => {
      const siteMainElement = document.querySelector(".top-canifa-s");
      if (siteMainElement) {
        siteMainElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    };

    const removableFilters = ref(<RemovableFilterInterface[]>[]);
    const onRemovableFiltersChanged = (filters) => {
      removableFilters.value = filters;
    };

    const onRemoveFilter = ({ id, value }) => {
      categoryFiltersRef.value?.doRemoveFilter?.({ id, value });
    };
    const onClearAllFilters = () => {
      categoryFiltersRef.value?.doClearFilters?.();
    };

    // Function to track product list view with Clevertap
    const trackProductListView = () => {
      if (
        !activeCategoryByUid.value ||
        !categoryId ||
        !process.client ||
        !window.clevertap
      )
        return;
      if (hasTrackedView.value) return; // Guard to prevent duplicate tracking

      hasTrackedView.value = true; // Set tracking flag immediately

      // Create tracking data
      const trackData = {
        data: {
          list_id: categoryId,
          list_name: activeCategoryByUid.value.name || "",
        },
        clevertap: window.clevertap,
      };

      // Call Clevertap event
      clevertapStore.productListViewdCleverTap(trackData);
    };

    // Track view only once per session using a flag
    const hasTrackedView = ref(false);

    // Simplify tracking logic - only track on initial category and products load
    watch(
      [() => activeCategoryByUid.value, () => products.value.length],
      ([newCategory, productsCount]) => {
        if (newCategory && productsCount > 0) {
          // Call directly rather than through debounced function
          trackProductListView();
        }
      },
      { immediate: false } // Set immediate to false to prevent duplicate calls on initial mount
    );

    const trackProductListFilter = (searchParams: any) => {
      if (!activeCategoryByUid.value || !categoryId) return;

      // Create base tracking data
      const trackData: any = {
        data: {
          list_id: categoryId,
          list_name: activeCategoryByUid.value.name || "",
        },
        clevertap: window.clevertap,
      };

      // Add sort information
      if (searchParams.sort) {
        let sortBy = "default";
        // Handle the sort format like "name_ASC" or "price_DESC"
        const sortParts = searchParams.sort.split("_");
        if (sortParts.length > 0) {
          const [sortField, sortDirection] = sortParts;
          if (sortField === "price" && sortDirection === "ASC") {
            sortBy = "price asc";
          } else if (sortField === "price" && sortDirection === "DESC") {
            sortBy = "price desc";
          } else if (sortField === "created") {
            sortBy = "newest";
          }
        }
        trackData.data.sort_by = sortBy;
      }

      // Process filters
      if (
        searchParams.filters &&
        Object.keys(searchParams.filters).length > 0
      ) {
        const filters = searchParams.filters;
        const filterTypes = [];

        // Category filter
        if (filters.category_id) {
          filterTypes.push("category");

          // Handle category filters as array
          if (Array.isArray(filters.category_id)) {
            filters.category_id.forEach((catId: string, index: number) => {
              // Find category name by ID if possible
              let catName = "";
              if (categoryTree.value) {
                const category = findCategoryByUid(categoryTree.value, catId);
                if (category) {
                  catName = category.name;
                }
              }
              trackData.data[`category_name_${index + 1}`] = catName || catId;
            });
          }
        }

        // Size filter
        if (filters.size) {
          filterTypes.push("size");

          // Handle size filters as array
          if (Array.isArray(filters.size)) {
            filters.size.forEach((size: string, index: number) => {
              trackData.data[`size_type_${index + 1}`] = size;
            });
          }
        }

        // Color filter
        if (filters.mastercolor) {
          filterTypes.push("color");

          // Handle color filters as array
          if (Array.isArray(filters.mastercolor)) {
            filters.mastercolor.forEach((color: string, index: number) => {
              trackData.data[`color_value_${index + 1}`] = color;
            });
          }
        }

        // Price filter
        if (filters.price) {
          filterTypes.push("price");

          // Handle price range format like "129000_202000"
          if (Array.isArray(filters.price) && filters.price.length > 0) {
            const priceRange = filters.price[0];
            const priceParts = priceRange.split("_");
            if (priceParts.length === 2) {
              const [from, to] = priceParts;
              trackData.data.price_value = `${from} - ${to}`;
            } else {
              trackData.data.price_value = priceRange;
            }
          }
        }

        // Sale filter
        if (filters.discount_percent) {
          filterTypes.push("sale");

          // Handle sale filters as array
          if (Array.isArray(filters.discount_percent)) {
            filters.discount_percent.forEach((sale: string, index: number) => {
              trackData.data[`sale_${index + 1}`] = sale;
            });
          }
        }

        // Add filter_by parameter with comma-separated list of filter types
        if (filterTypes.length > 0) {
          trackData.data.filter_by = filterTypes.join(", ");
        }
      }
      // Call Clevertap event
      clevertapStore.productListFilterCleverTap(trackData);
    };

    // Create a debounced version of the filter tracking function
    const debouncedTrackFilter = debounce((searchParams: any) => {
      trackProductListFilter(searchParams);
    }, 500);

    const lastTrackedParams = ref("");

    watch(
      () => products.value,
      (newResult, oldResult) => {
        if (
          process.client &&
          window.clevertap &&
          activeCategoryByUid.value &&
          newResult.length > 0
        ) {
          // Get current search params
          const searchParams = uiHelpers.getFacetsFromURL();

          // Stringify params to compare with last tracked params
          const paramsString = JSON.stringify(searchParams);

          // Check if filters or sort have been applied and it's different from last tracked
          const hasFilters =
            searchParams.filters &&
            Object.keys(searchParams.filters).length > 0;
          const hasSort = !!searchParams.sort;

          if (
            (hasFilters || hasSort) &&
            paramsString !== lastTrackedParams.value
          ) {
            lastTrackedParams.value = paramsString;
            debouncedTrackFilter(searchParams);
          }
        }
      },
      { deep: true, immediate: true }
    );

    // Watch for changes in products to track product list view
    watch(
      [() => activeCategoryByUid.value],
      ([newCategory]) => {
        if (process.client && window.clevertap && newCategory) {
          trackProductListView();
        }
      },
      { immediate: true }
    );

    return {
      leftMenu,
      activeIndexes,
      toggleActive,
      categoryUrlPath,
      categoryUid,
      onReloadProducts,
      onRemovableFiltersChanged,
      categoryFiltersRef,
      products,
      totalItems,
      sortBy,
      onRemoveFilter,
      onClearAllFilters,
      loading,
      loadingProduct,
      isLoadingMore,
      loadMoreProducts,
      removableFilters,
      isOpenFilterMobile,
    };
  },
});
